import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Autocomplete,
    FormControlLabel,
    Checkbox,
    Typography,
} from '@mui/material';
import { Organization, UserData } from 'utils';
import { adminGetAllOrganizations } from '../../../admin.organizations.api';
import { adminGetAllUsers } from '../../../admin.users.api';
import { debounce } from 'lodash';
import { CreateOrganizationParams } from '../organizations.api';

interface CreateOrganizationDialogProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (organizationData: CreateOrganizationParams) => void;
}

const ORGANIZATION_TYPES = [
    'travel',
    'club',
    'high-school',
    'athletic-training',
    'private',
    'professional',
    'college',
    'league',
    'personal',
];

const CreateOrganizationDialog: React.FC<CreateOrganizationDialogProps> = ({
    open,
    onClose,
    onSubmit,
}) => {
    const [name, setName] = useState('');
    const [zip, setZip] = useState('');
    const [type, setType] = useState('');
    const [associatedUserIsOwner, setAssociatedUserIsOwner] = useState(true);

    const [users, setUsers] = useState<UserData[]>([]);
    const [selectedUser, setSelectedUser] = useState<UserData | null>(null);

    const [referringOrganizations, setReferringOrganizations] = useState<Organization[]>([]);
    const [selectedReferringOrg, setSelectedReferringOrg] = useState<Organization | null>(null);

    const [parentOrganizations, setParentOrganizations] = useState<Organization[]>([]);
    const [selectedParentOrg, setSelectedParentOrg] = useState<Organization | null>(null);

    const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

    const fetchUsers = debounce((searchTerm: string) => {
        adminGetAllUsers({ name: searchTerm }, 500)
            .then((response) => setUsers(response.data.data))
            .catch((error) => console.error('Failed to fetch users', error));
    }, 300);

    const fetchReferringOrganizations = debounce((searchTerm: string) => {
        adminGetAllOrganizations(searchTerm, 500)
            .then((response) => setReferringOrganizations(response.data.data))
            .catch((error) => console.error('Failed to fetch referring organizations', error));
    }, 300);

    const fetchParentOrganizations = debounce((searchTerm: string) => {
        adminGetAllOrganizations(searchTerm, 500)
            .then((response) => setParentOrganizations(response.data.data))
            .catch((error) => console.error('Failed to fetch parent organizations', error));
    }, 300);

    useEffect(() => {
        if (open) {
            fetchUsers('');
            fetchReferringOrganizations('');
            fetchParentOrganizations('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const handleSubmit = () => {
        const errors: { [key: string]: string } = {};

        if (!name || name.trim().length < 2) {
            errors.name = 'Organization name must be at least 2 characters';
        }

        if (!zip || !/^\d{5}$/.test(zip)) {
            errors.zip = 'ZIP code must be exactly 5 digits';
        }

        if (!type) {
            errors.type = 'Organization type is required';
        }

        if (!selectedUser) {
            errors.user = 'Associated user is required';
        }

        setFormErrors(errors);

        if (Object.keys(errors).length > 0) {
            return;
        }

        const organizationData: CreateOrganizationParams = {
            name: name.trim(),
            zip: zip.trim(),
            type,
            associated_user: selectedUser!.uuid,
            associated_user_is_owner: associatedUserIsOwner,
            referring_organization: selectedReferringOrg?.uuid,
            parent_organization: selectedParentOrg?.uuid,
        };

        onSubmit(organizationData);
        handleClose();
    };

    const handleClose = () => {
        setName('');
        setZip('');
        setType('');
        setAssociatedUserIsOwner(true);
        setSelectedUser(null);
        setSelectedReferringOrg(null);
        setSelectedParentOrg(null);
        setFormErrors({});
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>Create Organization</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Create a new organization with the specified details. All fields marked with
                        * are required.
                    </Typography>

                    <TextField
                        label="Organization Name *"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        error={!!formErrors.name}
                        helperText={formErrors.name || 'Minimum 2 characters, maximum 200'}
                        fullWidth
                        size="small"
                    />

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            label="ZIP Code *"
                            value={zip}
                            onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, '');
                                if (value.length <= 5) {
                                    setZip(value);
                                }
                            }}
                            error={!!formErrors.zip}
                            helperText={formErrors.zip || 'Must be exactly 5 digits'}
                            sx={{ width: '50%' }}
                            size="small"
                            inputProps={{ maxLength: 5 }}
                        />

                        <FormControl sx={{ width: '50%' }} size="small" error={!!formErrors.type}>
                            <InputLabel>Organization Type *</InputLabel>
                            <Select
                                value={type}
                                onChange={(e) => setType(e.target.value)}
                                label="Organization Type *"
                            >
                                {ORGANIZATION_TYPES.map((orgType) => (
                                    <MenuItem key={orgType} value={orgType}>
                                        {orgType
                                            .split('-')
                                            .map(
                                                (word) =>
                                                    word.charAt(0).toUpperCase() + word.slice(1),
                                            )
                                            .join(' ')}
                                    </MenuItem>
                                ))}
                            </Select>
                            {formErrors.type && (
                                <Typography
                                    variant="caption"
                                    color="error"
                                    sx={{ mt: 0.5, ml: 1.5 }}
                                >
                                    {formErrors.type}
                                </Typography>
                            )}
                        </FormControl>
                    </Box>

                    <Typography variant="subtitle2" sx={{ mt: 2, fontWeight: 600 }}>
                        User Association
                    </Typography>

                    <FormControl error={!!formErrors.user}>
                        <Autocomplete
                            value={users.find((user) => user.uuid === selectedUser?.uuid) || null}
                            onChange={(event, newValue) => {
                                setSelectedUser(newValue);
                            }}
                            onInputChange={(event, newInputValue, reason) => {
                                if (reason !== 'reset') {
                                    fetchUsers(newInputValue);
                                }
                            }}
                            getOptionKey={(option) => option.uuid}
                            filterOptions={(options) => options}
                            size="small"
                            options={users}
                            getOptionLabel={(option) =>
                                `${option.first_name} ${option.last_name} (${option.email}) - ${option.uuid}`
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Associated User *"
                                    error={!!formErrors.user}
                                    helperText={
                                        formErrors.user ||
                                        'The user who will be associated with this organization'
                                    }
                                />
                            )}
                        />
                    </FormControl>

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={associatedUserIsOwner}
                                onChange={(e) => setAssociatedUserIsOwner(e.target.checked)}
                                size="small"
                            />
                        }
                        label="Associated user is owner"
                        sx={{
                            '& .MuiFormControlLabel-label': {
                                fontSize: '0.9rem',
                            },
                        }}
                    />

                    <Typography variant="subtitle2" sx={{ mt: 2, fontWeight: 600 }}>
                        Organizational Hierarchy (Optional)
                    </Typography>

                    <FormControl>
                        <Autocomplete
                            value={
                                referringOrganizations.find(
                                    (org) => org.uuid === selectedReferringOrg?.uuid,
                                ) || null
                            }
                            onChange={(event, newValue) => {
                                setSelectedReferringOrg(newValue);
                            }}
                            onInputChange={(event, newInputValue, reason) => {
                                if (reason !== 'reset') {
                                    fetchReferringOrganizations(newInputValue);
                                }
                            }}
                            getOptionKey={(option) => option.uuid}
                            filterOptions={(options) => options}
                            size="small"
                            options={referringOrganizations}
                            getOptionLabel={(option) => `${option.name} - ${option.uuid}`}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Referring Organization"
                                    helperText="The organization that referred this one"
                                />
                            )}
                        />
                    </FormControl>

                    <FormControl>
                        <Autocomplete
                            value={
                                parentOrganizations.find(
                                    (org) => org.uuid === selectedParentOrg?.uuid,
                                ) || null
                            }
                            onChange={(event, newValue) => {
                                setSelectedParentOrg(newValue);
                            }}
                            onInputChange={(event, newInputValue, reason) => {
                                if (reason !== 'reset') {
                                    fetchParentOrganizations(newInputValue);
                                }
                            }}
                            getOptionKey={(option) => option.uuid}
                            filterOptions={(options) => options}
                            size="small"
                            options={parentOrganizations}
                            getOptionLabel={(option) => `${option.name} - ${option.uuid}`}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Parent Organization"
                                    helperText="The parent organization in the hierarchy"
                                />
                            )}
                        />
                    </FormControl>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" color="primary">
                    Create Organization
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateOrganizationDialog;
